/**
 * Notificações nativas do SO para ligações Multiflow Voz
 * (aba em segundo plano / janela minimizada).
 */

const PERM_PROMPTED_KEY = "mfv_voice_notify_prompted";
const DEFAULT_ICON = "/favicon.ico";

let titleInterval = null;
let originalTitle = "";
let activeNotification = null;
let lastTagKey = "";

export function voiceNotificationsSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

export function voiceNotificationPermission() {
  if (!voiceNotificationsSupported()) return "unsupported";
  return Notification.permission;
}

/**
 * Pede permissão (só com gesto do usuário quando possível).
 * Idempotente se já granted/denied.
 * @returns {Promise<"granted"|"denied"|"default"|"unsupported">}
 */
export async function ensureVoiceNotificationPermission() {
  if (!voiceNotificationsSupported()) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";

  try {
    const result = await Notification.requestPermission();
    try {
      localStorage.setItem(PERM_PROMPTED_KEY, "1");
    } catch {
      /* ignore */
    }
    return result;
  } catch {
    return Notification.permission || "default";
  }
}

/**
 * Solicita permissão de forma suave: no primeiro uso de voz
 * (abrir softphone / click). Não re-pede se já perguntou e deny.
 */
export function maybeRequestVoiceNotifyOnGesture() {
  if (!voiceNotificationsSupported()) return;
  if (Notification.permission !== "default") return;
  let prompted = false;
  try {
    prompted = localStorage.getItem(PERM_PROMPTED_KEY) === "1";
  } catch {
    prompted = false;
  }
  // Se ainda default, tenta pedir no gesto (abrir softphone)
  if (!prompted || Notification.permission === "default") {
    ensureVoiceNotificationPermission().catch(() => {});
  }
}

function buildWhoLabel({ name, phone, peer } = {}) {
  const n = String(name || "").trim();
  const p = String(phone || "")
    .replace(/\D/g, "");
  const pe = String(peer || "").trim();
  if (n && p) return `${n} (${p})`;
  if (n) return n;
  if (p) return p;
  if (pe) return pe;
  return "WhatsApp";
}

/**
 * Fecha notificação ativa e para o piscar do título.
 */
export function clearVoiceDesktopAlert() {
  stopDocumentTitleFlash();
  try {
    if (activeNotification) {
      activeNotification.close();
      activeNotification = null;
    }
  } catch {
    /* ignore */
  }
  lastTagKey = "";
}

function stopDocumentTitleFlash() {
  if (titleInterval) {
    clearInterval(titleInterval);
    titleInterval = null;
  }
  if (originalTitle) {
    try {
      document.title = originalTitle;
    } catch {
      /* ignore */
    }
    originalTitle = "";
  }
}

/**
 * Alterna o título da aba enquanto toca (útil com várias abas abertas).
 */
export function startDocumentTitleFlash(alertText) {
  if (typeof document === "undefined") return;
  stopDocumentTitleFlash();
  originalTitle = document.title || "Multiflow";
  const flash = String(alertText || "📞 Ligação").slice(0, 48);
  let on = false;
  titleInterval = setInterval(() => {
    on = !on;
    try {
      document.title = on ? flash : originalTitle;
    } catch {
      /* ignore */
    }
  }, 900);
}

/**
 * @param {object} opts
 * @param {"incoming"|"transfer"|"ivr"} opts.kind
 * @param {string} [opts.callId]
 * @param {string} [opts.name]
 * @param {string} [opts.phone]
 * @param {string} [opts.peer]
 * @param {() => void} [opts.onClick] — focar softphone / aba
 */
export function showVoiceDesktopNotification(opts = {}) {
  const {
    kind = "incoming",
    callId = "",
    name = "",
    phone = "",
    peer = "",
    onClick,
  } = opts;

  const who = buildWhoLabel({ name, phone, peer });
  const isTransfer = kind === "transfer";
  const isIvr = kind === "ivr";
  const title = isTransfer
    ? "Transferência de ligação"
    : isIvr
      ? "URA — chamada recebida"
      : "Ligação recebida";
  const body = isTransfer
    ? `${who} — abra o softphone para atender`
    : isIvr
      ? `${who} — URA em atendimento (abra o Multiflow)`
      : `${who} está ligando`;

  // Título da aba sempre (não depende de permissão)
  startDocumentTitleFlash(
    isTransfer
      ? `🔁 Transferência · ${who}`
      : isIvr
        ? `URA · ${who}`
        : `📞 Ligação · ${who}`
  );

  if (!voiceNotificationsSupported()) return null;
  if (Notification.permission !== "granted") return null;

  const tag = `mfv-voice-${kind}-${callId || phone || who}`;
  // Evita spam: same call + mesmo texto; recria se o nome for resolvido
  const contentKey = `${tag}|${body}`;
  if (activeNotification && contentKey === lastTagKey) {
    return activeNotification;
  }
  lastTagKey = contentKey;

  try {
    if (activeNotification) {
      activeNotification.close();
    }
  } catch {
    /* ignore */
  }

  try {
    const n = new Notification(title, {
      body,
      tag,
      renotify: true,
      requireInteraction: true,
      icon: DEFAULT_ICON,
      badge: DEFAULT_ICON,
      // som costuma ser do app; alguns browsers ignoram
      silent: false,
    });
    activeNotification = n;

    n.onclick = () => {
      try {
        window.focus();
      } catch {
        /* ignore */
      }
      try {
        onClick?.();
      } catch {
        /* ignore */
      }
      try {
        n.close();
      } catch {
        /* ignore */
      }
    };

    n.onclose = () => {
      if (activeNotification === n) activeNotification = null;
    };

    return n;
  } catch (err) {
    console.warn("[voz] Notification falhou:", err?.message || err);
    return null;
  }
}
